import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cx } from './cx';

/** DOM attribute passthrough (role / aria-* / id / data-*) for the layout
 *  primitives, minus the props each component owns. */
type DOMRest = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'children'>;

/**
 * Layout primitives that encode the spacing RHYTHM from SPEC §1.2 so pages
 * stop hand-picking gaps:
 *   tight   = within a group (label ↔ control)
 *   snug    = a half-step (headers, dense lists) - fills the 8->16 gap
 *   group   = default between elements
 *   loose   = between sub-groups
 *   section = between major sections
 *
 * NOTE: Cluster (horizontal) intentionally runs one step tighter than Stack
 * (vertical) at the upper end - horizontal gaps read wider at the same px, so
 * the same name maps to a slightly smaller value on Cluster. Use the names, not
 * the numbers.
 */
type Gap = 'none' | 'tight' | 'snug' | 'group' | 'loose' | 'section';

const STACK_GAP: Record<Gap, string> = {
  none: 'gap-0',
  tight: 'gap-2',   // 8  - within a group
  snug: 'gap-3',    // 12 - half-step (header lockups, dense lists)
  group: 'gap-4',   // 16 - default between elements (team-liked)
  loose: 'gap-5',   // 20 - between sub-groups
  section: 'gap-8', // 32 - between major sections (was 48; team: too much vertical space)
};

export function Stack({
  gap = 'group',
  as,
  className,
  children,
  ...rest
}: {
  gap?: Gap;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & DOMRest) {
  const Tag = (as ?? 'div') as ElementType;
  return (
    <Tag className={cx('flex flex-col', STACK_GAP[gap], className)} {...rest}>
      {children}
    </Tag>
  );
}

const CLUSTER_GAP: Record<Gap, string> = {
  none: 'gap-0',
  tight: 'gap-2',  // 8
  snug: 'gap-2.5', // 10
  group: 'gap-3',  // 12
  loose: 'gap-4',  // 16
  section: 'gap-6', // 24
};

export function Cluster({
  gap = 'group',
  align = 'center',
  justify = 'start',
  wrap = true,
  as,
  className,
  children,
  ...rest
}: {
  gap?: Gap;
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between';
  wrap?: boolean;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & DOMRest) {
  const Tag = (as ?? 'div') as ElementType;
  const alignCls = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
  }[align];
  const justifyCls = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  }[justify];
  return (
    <Tag
      className={cx('flex', wrap && 'flex-wrap', alignCls, justifyCls, CLUSTER_GAP[gap], className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

const CONTAINER_MAX: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'max-w-[640px]',
  md: 'max-w-[960px]',
  lg: 'max-w-[1200px]',
};

export function Container({
  size = 'lg',
  as,
  className,
  children,
  ...rest
}: {
  size?: 'sm' | 'md' | 'lg';
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & DOMRest) {
  const Tag = (as ?? 'div') as ElementType;
  return (
    <Tag className={cx('mx-auto w-full px-6', CONTAINER_MAX[size], className)} {...rest}>
      {children}
    </Tag>
  );
}
